// src/pages/MyProfile.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Breadcrumbs,
    Tabs,
    Tab,
    Paper,
    useMediaQuery,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Link as MuiLink
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import useAuthCheck from "../hooks/useAuthCheck";
import { useOutletContext, useNavigate, useLocation } from "react-router-dom";

// Import komponen tab yang sudah dipisah
import ProfileContent from "./MyProfile/components/ProfileContent";
import SettingContent from "./MyProfile/components/SettingContent";


// Definisikan konfigurasi tab untuk halaman MyProfile
const tabConfigurations = [
    {
        label: "Profile",
        component: ProfileContent,
        pathSegment: "profile"
    },
    {
        label: "Setting",
        component: SettingContent,
        pathSegment: "setting"
    },
];

function MyProfile() {
    const { user, loading } = useAuthCheck();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();
    const location = useLocation();
    const { breadcrumbs } = useOutletContext();

    const [tabIndex, setTabIndex] = useState(() => {
        const pathSegments = location.pathname.split('/');
        const lastSegment = pathSegments[pathSegments.length - 1];
        const initialIndex = tabConfigurations.findIndex(tab => tab.pathSegment === lastSegment);
        return initialIndex !== -1 ? initialIndex : 0;
    });

    useEffect(() => {
        const currentPathSegment = tabConfigurations[tabIndex].pathSegment;
        if (!location.pathname.endsWith(`/${currentPathSegment}`)) {
            navigate(`/my-profile/${currentPathSegment}`, { replace: true });
        }
    }, [tabIndex, navigate, location.pathname]);


    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const CurrentTabComponent = tabConfigurations[tabIndex].component;

    return (
        <Box sx={{ pb: isMobile ? 8 : 0 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {breadcrumbs && breadcrumbs.map((crumb, index) => (
                    crumb.path ? (
                        <MuiLink
                            key={index}
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate(crumb.path)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {crumb.label}
                        </MuiLink>
                    ) : (
                        <Typography key={index} color="text.primary">
                            {crumb.label}
                        </Typography>
                    )
                ))}
            </Breadcrumbs>

            {/* Layout */}
            <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 4, p:0 }}>
                {/* Konten utama */}
                <Box sx={{ flex: 1, p:0 }}>
                    <Card sx={{p:0}}>
                        <CardContent sx={{p:0, '&:last-child': { pb: 0 }}}>
                            {/* Render komponen tab yang sesuai dan teruskan props user dan theme */}
                            <CurrentTabComponent user={user} theme={theme} />
                        </CardContent>
                    </Card>
                </Box>

                {/* Sidebar desktop */}
                {!isMobile && (
                    <Paper
                        elevation={1}
                        sx={{
                            alignSelf: "flex-start",
                        }}
                    >
                        <Tabs
                            orientation="vertical"
                            value={tabIndex}
                            onChange={handleTabChange}
                            TabIndicatorProps={{
                                sx: {
                                    left: 0,
                                    right: "auto",
                                    width: "4px",
                                    bgcolor: "primary.main",
                                },
                            }}
                            sx={{
                                width: 350,
                                borderLeft: 1,
                                borderColor: "divider",
                                ".MuiTab-root": {
                                    alignItems: "flex-start",
                                    justifyContent: "flex-start",
                                    px: 2,
                                },
                            }}
                        >
                            {/* Loop melalui konfigurasi tab untuk sidebar */}
                            {tabConfigurations.map((tab, index) => (
                                <Tab key={index} label={tab.label} />
                            ))}
                        </Tabs>
                    </Paper>
                )}
            </Box>

            {/* Bottom Tabs (Mobile) */}
            {isMobile && (
                <Paper
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        borderTop: 1,
                        borderColor: "divider",
                        zIndex: 1000,
                    }}
                    elevation={3}
                >
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        TabIndicatorProps={{
                            sx: { bgcolor: "primary.main", height: 3 },
                        }}
                        centered
                    >
                        {/* Loop melalui konfigurasi tab untuk mobile */}
                        {tabConfigurations.map((tab, index) => (
                            <Tab key={index} label={tab.label} />
                        ))}
                    </Tabs>
                </Paper>
            )}
        </Box>
    );
}

export default MyProfile;
